import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import { STAUTES } from "../../../../utils/constant";
import { errorToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import { GET_ALL_TIMESHEETS } from "../../../../utils/services/apiPath";
import { getApi } from "../../../../utils/services";
import { employeeTimeSheetFormName } from "../../../../utils/formHelper";
import { employeesTimeSheetPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";
import useColumn from "../../../../utils/customHooks/useColumns";

export default function EmployeeTimeSheets() {
  let { id } = useParams();
  const [showGrid] = useState(true);
  const {
    currentPage,
    pageSize,
    data: timeSheetData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { [employeeTimeSheetFormName]: timeSheetPermission } = usePermission(employeesTimeSheetPermissionSelector, employeeTimeSheetFormName);

  const getAllTimeSheet = useCallback(async () => {
    setStatus(STAUTES.LOADING);

    const skip = searchString ? 0 : currentPage * pageSize;
    const orderDir = sortModel?.orderByAscending ? "asc" : "desc";
    const orderCol = sortModel?.orderCol ?? "workedDate";
    const sanitizedSearch = searchString?.trim().toLowerCase().replace(/'/g, "''");

    const params = {
      $top: pageSize,
      $skip: skip,
      $count: true,
      $orderby: `${orderCol} ${orderDir}`,
      ...(sanitizedSearch
        ? {
            $filter: `contains(tolower(projectName), '${sanitizedSearch}') or contains(tolower(jobTitle), '${sanitizedSearch}')`
          }
        : {})
    };

    const responseData = await getApi(
      GET_ALL_TIMESHEETS.replace(":employeeId", id),
      { params }
    );

    setStatus(STAUTES.IDLE);
    if (responseData?.error) return errorToast(responseData.error);

    const items = responseData?.data ?? [];
    const totalRecords = responseData?.totalRecords ?? items.length;

    const dataWithIds = items.map((item, index) => ({
      ...item,
      id: index + 1 + skip
    }));
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: dataWithIds, rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, id, setPageState, setStatus]);

  useEffect(() => {
    getAllTimeSheet();
  }, [getAllTimeSheet]);

  const columns = useColumn([
    {
      field: "projectName",
      headerName: "Project Name"
    },
    {
      field: "jobTitle",
      headerName: "Position"
    },
    {
      field: "totalStandardHours",
      headerName: "Total Standard Hours"
    },
    {
      field: "totalOtHours",
      headerName: "Total OT Hours"
    },
    {
      field: "totalDtHours",
      headerName: "Total Double-time Hours"
    },
    {
      field: "workedDate",
      headerName: "Worked Date",
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString("en-US") : ""
    }
  ], true);

  return (
    <TabGridAndFormLayout showGrid={showGrid} title={"Timesheets"} canView={timeSheetPermission.canView}>
      {showGrid && (
        <ProGrid
          options={{
            getRowId: row => row?.id,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          title="Timesheets"
          loading={status === STAUTES.LOADING}
          columns={columns}
          hideAddButton={true}
          hideExport={!timeSheetPermission.canExport}
          rows={timeSheetData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        />
      )}
    </TabGridAndFormLayout>
  );
}
